import { assertValidCanonicalMatchOutcome } from '@djgg/core';
import type { RecordedMatchRead, RecordedMatchResult } from './types.js';

/** Mirrors the online clients' endpoint precedence, with HTTP for the public read API. */
export function recordedMatchApiBase(search = typeof location === 'undefined' ? '' : location.search,
  env: Record<string, unknown> = import.meta.env): string {
  const endpoint = new URLSearchParams(search).get('server') || env.VITE_DJGG_SERVER
    || (env.DEV ? 'ws://localhost:2567' : 'wss://djgg-game-server-749639621430.us-central1.run.app');
  const url = new URL(String(endpoint));
  if (url.protocol === 'ws:') url.protocol = 'http:';
  if (url.protocol === 'wss:') url.protocol = 'https:';
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('Invalid game server endpoint');
  }
  return url.toString().replace(/\/+$/, '');
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid public result');
  return value as Record<string, unknown>;
}
function nonnegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
}
function pathId(value: string): boolean {
  return /^[A-Za-z0-9._:-]{1,128}$/.test(value) && value !== '.' && value !== '..';
}

/** Select only public fields, checking identity and terminal semantics before rendering. */
export function decodeRecordedMatch(value: unknown, creationId: string, matchId: string): RecordedMatchRead {
  const body = object(value);
  if (typeof body.status !== 'string') throw new Error('Invalid public result');
  if (['not-found', 'pending', 'unavailable', 'conflict'].includes(body.status)) {
    return { status: body.status as 'not-found' | 'pending' | 'unavailable' | 'conflict' };
  }
  if (body.status !== 'result' && body.status !== 'no-contest') throw new Error('Invalid public result');
  const row = object(body.result);
  if (row.creationId !== creationId || row.matchId !== matchId) return { status: 'conflict' };
  const teams = row.teamBySlot;
  const scores = row.scores;
  const players = row.players;
  if (row.schemaVersion !== 2 || row.kind !== 'composition-public-result-v2'
    || row.ranked !== false || !Number.isSafeInteger(row.version) || Number(row.version) < 1
    || typeof row.publicationDigest !== 'string' || !/^[a-f0-9]{64}$/.test(row.publicationDigest)
    || !nonnegative(row.durationMs) || !nonnegative(row.endedAtMs)
    || !Number.isSafeInteger(row.terminalTick) || Number(row.terminalTick) < 0
    || object(row.playback).available !== false
    || !Array.isArray(teams) || !Array.isArray(scores) || !Array.isArray(players)
    || players.length < 1 || players.length > 16 || teams.length !== players.length || scores.length !== players.length
    || scores.some(score => typeof score !== 'number' || !Number.isFinite(score))) throw new Error('Invalid public result');
  assertValidCanonicalMatchOutcome(row.outcome, teams);
  if ((body.status === 'no-contest') !== (row.outcome.kind === 'no-contest')) throw new Error('Invalid public result');
  const safePlayers = players.map((value, index) => {
    const player = object(value);
    if (player.slot !== index || typeof player.nickname !== 'string' || !player.nickname.trim()
      || player.nickname.length > 128) throw new Error('Invalid public player');
    return { slot: index, nickname: player.nickname };
  });
  const result: RecordedMatchResult = {
    schemaVersion: 2, kind: 'composition-public-result-v2', creationId, matchId,
    version: Number(row.version), publicationDigest: row.publicationDigest, ranked: false,
    durationMs: row.durationMs, endedAtMs: row.endedAtMs, terminalTick: Number(row.terminalTick),
    outcome: row.outcome, scores: [...scores], teamBySlot: [...teams], players: safePlayers,
    playback: { available: false },
  };
  return { status: body.status, result };
}

export function createRecordedMatchClient(base: () => string = recordedMatchApiBase,
  request: typeof fetch = fetch): (creationId: string, matchId: string) => Promise<RecordedMatchRead> {
  return async (creationId, matchId) => {
    if (!pathId(creationId) || !/^[A-Za-z0-9]/.test(creationId) || !pathId(matchId)) return { status: 'not-found' };
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await request(`${base()}/v2/creations/${encodeURIComponent(creationId)}/matches/${encodeURIComponent(matchId)}`,
        { signal: controller.signal, cache: 'no-store', credentials: 'omit' });
      const payload = await response.json();
      const wireStatus = object(payload).status;
      const read = decodeRecordedMatch(payload, creationId, matchId);
      const expected = wireStatus === 'not-found' ? 404 : wireStatus === 'pending' ? 202
        : wireStatus === 'conflict' ? 409 : wireStatus === 'unavailable' ? 503
          : wireStatus === 'result' || wireStatus === 'no-contest' ? 200 : -1;
      if (response.status !== expected) return { status: 'unavailable' };
      return read;
    } catch {
      return { status: 'unavailable' };
    } finally {
      clearTimeout(timeout);
    }
  };
}
